import React, { useCallback, useState } from 'react';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { Download, Loader2, Check, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatBookingRef, shopInitials } from '../lib/bookingRef';

/**
 * DataExport — shop_admin-only widget that lets an owner download their
 * shop's data as a ZIP of CSVs.
 *
 * Everything runs client-side against Supabase (RLS-scoped to the caller's
 * shop) so we never hit FastAPI memory limits on heavy archives.
 *
 * Contents of the ZIP:
 *   /bookings.csv     — every booking with a formatted human ref
 *   /customers.csv    — every customer row (name, phone, email, consent)
 *   /payments.csv     — deposits, in-shop balances, refunds — one row per booking
 *   /messages.csv     — sms_log entries
 *   /settings.csv     — shop row (single row) + services + barbers, stacked
 *   /README.txt       — human-readable manifest with row counts + timestamp
 *
 * After a successful export we insert one row into `data_operations_log`
 * for audit purposes.
 */

const csvBlob = (rows) => Papa.unparse(rows, { header: true, quotes: true });

const iso = (d) => (d ? new Date(d).toISOString() : '');

const safeFilename = (name) => {
  const base = (name || 'shop').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `instacut-${base || 'shop'}-${stamp}.zip`;
};

export const DataExport = ({ shopId }) => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'running' | 'done' | 'error'
  const [progress, setProgress] = useState('');
  const [error, setError]     = useState(null);
  const [lastCounts, setLastCounts] = useState(null);

  const run = useCallback(async () => {
    if (!shopId) return;
    setStatus('running');
    setError(null);
    setProgress('Preparing…');

    try {
      // ---- 1. Fetch everything in parallel (RLS scopes each read) ------
      setProgress('Fetching data…');

      // Try bookings SELECT with booking_number first; fall back if the
      // migration hasn't been applied yet.
      const BOOKINGS_FULL = `
        id, booking_number, starts_at, ends_at, status, channel, created_at,
        deposit_paid, deposit_amount, paid_in_full,
        payment_status, balance_paid_method, balance_paid_amount, balance_paid_at,
        stripe_payment_intent_id, refund_status, refund_stripe_id,
        customer:customers(id, name, phone, email),
        service:services(id, name, price),
        barber:barbers(id, name)
      `;
      const BOOKINGS_LEGACY = `
        id, starts_at, ends_at, status, channel, created_at,
        deposit_paid, deposit_amount, paid_in_full,
        payment_status, balance_paid_method, balance_paid_amount, balance_paid_at,
        stripe_payment_intent_id, refund_status, refund_stripe_id,
        customer:customers(id, name, phone, email),
        service:services(id, name, price),
        barber:barbers(id, name)
      `;

      const [shopRes, servicesRes, barbersRes, customersRes, smsRes] = await Promise.all([
        supabase.from('shops').select('*').eq('id', shopId).maybeSingle(),
        supabase.from('services')
          .select('id, name, price, duration_min, description, active, created_at')
          .eq('shop_id', shopId).order('created_at', { ascending: true }),
        supabase.from('barbers')
          .select('id, name, email, phone, active, created_at')
          .eq('shop_id', shopId).order('created_at', { ascending: true }),
        supabase.from('customers')
          .select('id, name, phone, email, marketing_consent, created_at')
          .eq('shop_id', shopId).order('created_at', { ascending: true }),
        supabase.from('sms_log')
          .select('id, booking_id, customer_id, to_phone, body, template_key, provider, status, sent_at, delivered_at')
          .eq('shop_id', shopId).order('sent_at', { ascending: true }),
      ]);

      let bookingsRes = await supabase.from('bookings')
        .select(BOOKINGS_FULL)
        .eq('shop_id', shopId).order('starts_at', { ascending: true });
      if (bookingsRes.error && /booking_number/.test(bookingsRes.error.message || '')) {
        bookingsRes = await supabase.from('bookings')
          .select(BOOKINGS_LEGACY)
          .eq('shop_id', shopId).order('starts_at', { ascending: true });
      }

      const errs = [shopRes, servicesRes, barbersRes, customersRes, bookingsRes, smsRes]
        .map((r) => r.error).filter(Boolean);
      // sms_log may not exist on legacy DBs — that specific error is fine.
      const fatal = errs.filter((e) => !/sms_log/.test(e.message || ''));
      if (fatal.length) throw fatal[0];

      const shop = shopRes.data || {};
      const services = servicesRes.data || [];
      const barbers = barbersRes.data || [];
      const customers = customersRes.data || [];
      const bookings = bookingsRes.data || [];
      const sms = smsRes.error ? [] : (smsRes.data || []);

      // ---- 2. Shape CSVs -----------------------------------------------
      setProgress('Building CSVs…');
      const initials = shopInitials(shop.name);

      const bookingRows = bookings.map((b) => ({
        ref: formatBookingRef({
          shopName: shop.name,
          startsAt: b.starts_at,
          bookingNumber: b.booking_number,
          uuid: b.id,
        }),
        booking_number: b.booking_number,
        uuid: b.id,
        starts_at: iso(b.starts_at),
        ends_at: iso(b.ends_at),
        status: b.status,
        channel: b.channel,
        customer_name: b.customer?.name || '',
        customer_phone: b.customer?.phone || '',
        customer_email: b.customer?.email || '',
        service_name: b.service?.name || '',
        service_price: b.service?.price ?? '',
        barber_name: b.barber?.name || '',
        payment_status: b.payment_status || '',
        deposit_paid: b.deposit_paid ? 'yes' : 'no',
        deposit_amount: b.deposit_amount ?? '',
        paid_in_full: b.paid_in_full ? 'yes' : 'no',
        balance_paid_method: b.balance_paid_method || '',
        balance_paid_amount: b.balance_paid_amount ?? '',
        balance_paid_at: iso(b.balance_paid_at),
        refund_status: b.refund_status || '',
        created_at: iso(b.created_at),
      }));

      const customerRows = customers.map((c) => ({
        id: c.id,
        name: c.name,
        phone: c.phone,
        email: c.email,
        marketing_consent: c.marketing_consent ? 'yes' : 'no',
        created_at: iso(c.created_at),
      }));

      // Payments = one row per money movement on a booking (deposit / balance / refund).
      const paymentRows = [];
      bookings.forEach((b) => {
        const ref = formatBookingRef({
          shopName: shop.name,
          startsAt: b.starts_at,
          bookingNumber: b.booking_number,
          uuid: b.id,
        });
        if (b.paid_in_full && b.deposit_amount) {
          paymentRows.push({
            booking_ref: ref, kind: 'paid_in_full', method: 'card',
            amount: b.deposit_amount, currency: shop.currency || 'GBP',
            stripe_id: b.stripe_payment_intent_id || '', occurred_at: iso(b.created_at),
          });
        } else if (b.deposit_paid && b.deposit_amount) {
          paymentRows.push({
            booking_ref: ref, kind: 'deposit', method: 'card',
            amount: b.deposit_amount, currency: shop.currency || 'GBP',
            stripe_id: b.stripe_payment_intent_id || '', occurred_at: iso(b.created_at),
          });
        }
        if (b.balance_paid_amount) {
          paymentRows.push({
            booking_ref: ref, kind: 'balance', method: b.balance_paid_method || '',
            amount: b.balance_paid_amount, currency: shop.currency || 'GBP',
            stripe_id: '', occurred_at: iso(b.balance_paid_at),
          });
        }
        if (b.refund_status === 'refunded' || b.refund_stripe_id) {
          paymentRows.push({
            booking_ref: ref, kind: 'refund', method: 'card',
            amount: b.deposit_amount || '', currency: shop.currency || 'GBP',
            stripe_id: b.refund_stripe_id || '', occurred_at: iso(b.created_at),
          });
        }
      });

      const messageRows = sms.map((m) => ({
        id: m.id,
        booking_id: m.booking_id || '',
        customer_id: m.customer_id || '',
        to_phone: m.to_phone,
        template_key: m.template_key,
        body: m.body,
        provider: m.provider,
        status: m.status,
        sent_at: iso(m.sent_at),
        delivered_at: iso(m.delivered_at),
      }));

      // Settings CSV = one row per (section, key, value) so it stays flat.
      const settingsRows = [];
      const pushSetting = (section, key, value) =>
        settingsRows.push({ section, key, value: value == null ? '' : String(value) });
      Object.entries(shop || {}).forEach(([k, v]) => {
        if (v && typeof v === 'object') pushSetting('shop', k, JSON.stringify(v));
        else pushSetting('shop', k, v);
      });
      services.forEach((s) => {
        pushSetting('service', `${s.name} · price`, s.price);
        pushSetting('service', `${s.name} · duration_min`, s.duration_min);
      });
      barbers.forEach((b) => {
        pushSetting('barber', `${b.name} · email`, b.email);
        pushSetting('barber', `${b.name} · phone`, b.phone);
        pushSetting('barber', `${b.name} · active`, b.active);
      });

      // Daily earnings — same rules as the on-screen EarningsCard: only
      // paid_in_full bookings count, amount = deposit_amount + balance_paid_amount,
      // cash = balance-cash portion, card = everything else.
      const earningsMap = new Map(); // key = `${date}::${barber_id}`
      bookings.forEach((b) => {
        if (!b.paid_in_full) return;
        if (b.status === 'cancelled' || b.status === 'no_show') return;
        const dep  = Number(b.deposit_amount || 0);
        const bal  = Number(b.balance_paid_amount || 0);
        const isCashBal = b.balance_paid_method === 'cash';
        const cash = isCashBal ? bal : 0;
        const card = dep + (isCashBal ? 0 : bal);
        const d = new Date(b.starts_at).toISOString().slice(0, 10);
        const bId = b.barber?.id || '';
        const key = `${d}::${bId}`;
        const cur = earningsMap.get(key) || {
          date: d,
          barber_id: bId,
          barber_name: b.barber?.name || '',
          gross_earnings: 0, cash: 0, card: 0, completed_bookings: 0,
        };
        cur.gross_earnings += dep + bal;
        cur.cash += cash;
        cur.card += card;
        cur.completed_bookings += 1;
        earningsMap.set(key, cur);
      });
      // Add a synthetic shop-total row per date.
      const dailyTotals = new Map();
      Array.from(earningsMap.values()).forEach((r) => {
        const t = dailyTotals.get(r.date) || {
          date: r.date, barber_id: '', barber_name: '— shop total —',
          gross_earnings: 0, cash: 0, card: 0, completed_bookings: 0,
        };
        t.gross_earnings += r.gross_earnings;
        t.cash += r.cash;
        t.card += r.card;
        t.completed_bookings += r.completed_bookings;
        dailyTotals.set(r.date, t);
      });
      const earningsRows = [
        ...Array.from(earningsMap.values()),
        ...Array.from(dailyTotals.values()),
      ].sort((a, b) => a.date.localeCompare(b.date) || a.barber_name.localeCompare(b.barber_name));

      // ---- 3. Zip it ---------------------------------------------------
      setProgress('Packaging ZIP…');
      const zip = new JSZip();
      zip.file('bookings.csv',       csvBlob(bookingRows));
      zip.file('customers.csv',      csvBlob(customerRows));
      zip.file('payments.csv',       csvBlob(paymentRows));
      zip.file('daily_earnings.csv', csvBlob(earningsRows));
      zip.file('messages.csv',       csvBlob(messageRows));
      zip.file('settings.csv',       csvBlob(settingsRows));
      zip.file('README.txt', [
        `Instacut data export`,
        `Shop: ${shop.name || '(unnamed)'}  [${initials}]`,
        `Generated: ${new Date().toISOString()}`,
        `--- Row counts ---`,
        `bookings.csv         ${bookingRows.length}`,
        `customers.csv        ${customerRows.length}`,
        `payments.csv         ${paymentRows.length}`,
        `daily_earnings.csv   ${earningsRows.length}`,
        `messages.csv         ${messageRows.length}`,
        `settings.csv         ${settingsRows.length}`,
      ].join('\n'));

      const blob = await zip.generateAsync({ type: 'blob' });

      // ---- 4. Trigger download ----------------------------------------
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = safeFilename(shop.name);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);

      // ---- 5. Audit log (best-effort — never blocks download) ---------
      const counts = {
        bookings: bookingRows.length,
        customers: customerRows.length,
        payments: paymentRows.length,
        daily_earnings: earningsRows.length,
        messages: messageRows.length,
        settings: settingsRows.length,
      };
      try {
        const { data: sess } = await supabase.auth.getUser();
        await supabase.from('data_operations_log').insert({
          shop_id: shopId,
          actor_id: sess?.user?.id || null,
          operation: 'export',
          tables: ['bookings', 'customers', 'payments', 'daily_earnings', 'messages', 'settings'],
          row_counts: counts,
        });
      } catch (_e) { /* data_operations_log table may not exist yet — ignore */ }

      setLastCounts(counts);
      setStatus('done');
      setProgress('');
    } catch (e) {
      console.error('Data export failed', e);
      setError(e.message || 'Export failed');
      setStatus('error');
      setProgress('');
    }
  }, [shopId]);

  return (
    <div className="panel" data-testid="data-export-panel" style={{ marginTop: 14 }}>
      <div className="ph">
        <h3>Export my data</h3>
        <span style={{ fontSize: 12, color: 'var(--mute)' }}>CSV · ZIP</span>
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--mute)', lineHeight: 1.5 }}>
          Download a ZIP archive containing your bookings, customers, payments,
          messages and settings as CSV files. Everything is scoped to this shop
          and generated in your browser — nothing leaves your device.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn primary"
            onClick={run}
            disabled={status === 'running' || !shopId}
            data-testid="export-download-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            {status === 'running'
              ? <><Loader2 size={14} className="spin" /> {progress || 'Working…'}</>
              : <><Download size={14} /> Download my data</>}
          </button>

          {status === 'done' && lastCounts && (
            <span
              data-testid="export-done"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--green, #16a34a)', fontSize: 12 }}
            >
              <Check size={14} /> Downloaded ·{' '}
              {lastCounts.bookings} bookings, {lastCounts.customers} customers,{' '}
              {lastCounts.payments} payments
            </span>
          )}

          {status === 'error' && (
            <span
              data-testid="export-error"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--red, #dc2626)', fontSize: 12 }}
            >
              <AlertCircle size={14} /> {error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataExport;
